// lib
import moment from "moment";
// src
import { getTrackedRange, updateFocusInView } from "utils/ranges";
import { MAX_SMALL_WIDTH, MINUTE } from "config/constants";
import { hydrateSlice } from "actions/system";
import * as actions from "actions/table";
import { recompute } from "actions/events";
import * as eventActions from "actions/events";
import { buildStorageAdapters } from "utils/storage";
import {
  updateLoadingInRange,
  updateLoadingsFromRequest,
  updateLoadingsFromResponse,
} from "./processors";

const maxAge = 30 * MINUTE;
const whitelist = ["focus", "view"];
const [hydrate, save] = buildStorageAdapters("table", { maxAge, whitelist });

const now = Date.now();
const DEFAULT_STATE = {
  // Loading
  isLoadingInRange: false,
  queue: [], // used to count requests and responses: are we loading?
  // Main UI messages
  notManagedByGeode: false,
  // Calendar settings
  updatedFromPicker: false,
  focus: now, // middle of the range of calendar (not timeline) views
  dateStart: moment(now).startOf("week").valueOf(), // used to fetch events within current scope
  dateEnd: moment(now).endOf("week").valueOf(), // used to fetch events within current scope
  view: window.innerWidth <= MAX_SMALL_WIDTH ? "fiveDays" : "week",
  // UX
  title: null,
  titleLink: null,
};

const updateTitleAndLink = (enableds) => {
  if (enableds && enableds.length === 1) {
    const { name, link } = enableds[0];
    return { title: name, titleLink: link };
  }
  return { title: null, titleLink: null };
};

export default (state = DEFAULT_STATE, { type, payload }) => {
  // order actions by frequency
  switch (type) {
    case recompute.toString(): {
      return {
        ...state,
        ...updateTitleAndLink(payload.enableds),
      };
    }
    case actions.updateRange.toString(): {
      // previous state
      const { dateStart, dateEnd } = payload;
      // visible range that may be larger than unit (for months: a few days before or after)
      const newDateStart = moment(dateStart).startOf("day").valueOf();
      const newDateEnd = moment(dateEnd).endOf("day").valueOf();

      const newFocus = updateFocusInView(state, { newDateEnd, newDateStart });
      const newIsLoadingInRange = updateLoadingInRange(state.queue, {
        dateStart: moment(newDateStart).toISOString(),
        dateEnd: moment(newDateEnd).toISOString(),
      });
      return save({
        ...state,
        dateStart: newDateStart,
        dateEnd: newDateEnd,
        focus: newFocus,
        isLoadingInRange: newIsLoadingInRange,
        updatedFromPicker: false,
      });
    }
    case actions.updateFocus.toString(): {
      let { focus: newFocus } = payload;
      const { queue, view } = state;
      if (view === "timeline") {
        newFocus = moment(newFocus).add(8, "h").valueOf();
      }
      const [newDateStart, newDateEnd] = getTrackedRange(newFocus, view);
      const newIsLoadingInRange = updateLoadingInRange(queue, {
        dateStart: moment(newDateStart).toISOString(),
        dateEnd: moment(newDateEnd).toISOString(),
      });
      return save({
        ...state,
        dateStart: newDateStart,
        dateEnd: newDateEnd,
        focus: newFocus,
        isLoadingInRange: newIsLoadingInRange,
        updatedFromPicker: true,
      });
    }
    case eventActions.eventsRequest.toString(): {
      return {
        ...state,
        ...updateLoadingsFromRequest(state, payload),
      };
    }
    case eventActions.eventsSuccess.toString(): {
      const { query, notManagedByGeode: newNotManagedByGeode } = payload.meta;
      return {
        ...state,
        ...updateLoadingsFromResponse(state, query),
        notManagedByGeode: newNotManagedByGeode || false,
      };
    }
    case actions.updateView.toString(): {
      return save({
        ...state,
        view: payload,
      });
    }
    case hydrateSlice.toString(): {
      const hydrated = hydrate(state, payload);
      const { focus, view } = hydrated;
      const [dateStart, dateEnd] = getTrackedRange(focus, view);
      return { ...hydrated, dateStart, dateEnd };
    }
    case eventActions.eventsFailure.toString(): {
      const query = payload;
      return {
        ...state,
        ...updateLoadingsFromResponse(state, query),
      };
    }
    default:
      return state;
  }
};
